import { useState, useEffect } from "react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

import {
  updateProfileType,
  getVideosExamples,
  getVideosTips,
  getVideosPages
} from "../../../../../service";
import { Button, InputSelect } from "../../../../form";

const VideoEditor = ({ org, profile, onSubmit = () => {} }) => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [examplesOptions, setExamplesOptions] = useState([]);
  const [tipsOptions, setTipsOptions] = useState([]);
  const [pagesOptions, setPagesOptions] = useState([]);
  const [exampleVideo, setExampleVideo] = useState(profile?.exampleVideo || null);
  const [videos, setVideos] = useState(profile?.videos || []);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const fetchOptions = async () => {
    if (!org?._id) return;
    try {
      setLoadingOptions(true);
      const [examplesRes, tipsRes, pagesRes] = await Promise.all([
        getVideosExamples(org._id),
        getVideosTips(org._id),
        getVideosPages(org._id)
      ]);

      setExamplesOptions(
        (examplesRes.data || [])
          .filter((item) => !item.deleted)
          .map((item) => ({
            value: item._id,
            label: item.referenceTitle
          }))
      );
      setTipsOptions(
        (tipsRes.data || [])
          .filter((item) => !item.deleted)
          .map((item) => ({
            value: item._id,
            label: item.referenceTitle
          }))
      );
      setPagesOptions(
        (pagesRes.data || [])
          .filter((item) => !item.deleted)
          .map((item) => ({
            value: item._id,
            label: `${item.referenceTitle} (Index: ${item.index})`
          }))
      );
    } catch (error) {
      console.error("Failed to fetch video options:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchOptions();
  }, [org?._id]);

  const handleAddVideoItem = () => {
    setVideos([...videos, { videoPage: null, videoTips: null }]);
  };

  const handleUpdateVideoItem = (index, field, value) => {
    const updated = [...videos];
    updated[index] = { ...updated[index], [field]: value };
    setVideos(updated);
  };

  const handleDeleteVideoItem = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async () => {
    if (!profile?._id) return;

    try {
      setIsFormSubmitting(true);
      await updateProfileType(org._id, {
        _id: profile._id,
        type: "videosInformation",
        exampleVideo,
        videos
      });
      toast.success("Videos Information saved successfully.");
      onSubmit();
    } catch (error) {
      toast.error("Failed to save the form.");
      console.error("Failed to save the form: ", error);
    } finally {
      setIsFormSubmitting(false);
    }
  };

  const handleRemoveExampleVideo = () => {
    setExampleVideo(null);
  };

  return (
    <>
      <div>
        {/* Example Video Selection */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-bold text-lg mb-3">Example Video</h3>
          <InputSelect
            label="Select Example Video"
            placeholder={loadingOptions ? "Loading..." : "Select an example video"}
            options={examplesOptions}
            value={examplesOptions.find((opt) => opt.value === exampleVideo) || null}
            onChange={(selected) => setExampleVideo(selected?.value || null)}
            isDisabled={loadingOptions}
          />

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleRemoveExampleVideo}
              className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded-md text-sm hover:bg-[#E63F00]">
              <TrashIcon className="w-4 h-4" />
              Remove Example Video
            </button>
          </div>
        </div>

        <div className="border-b-2 my-5"></div>

        {/* Videos List */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-lg">Videos</h3>
          </div>

          {videos.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2">
              <p className="text-gray-500 text-sm">No videos added yet.</p>
              <button
                type="button"
                onClick={handleAddVideoItem}
                className="flex items-center gap-1 px-3 py-1 bg-[#606060] text-white rounded-md text-sm hover:bg-[#505050]">
                <PlusIcon className="w-4 h-4" />
                Add Video
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {videos.map((item, index) => (
                <div key={index} className="p-3 bg-white border rounded-lg shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 grid grid-cols-1 gap-3">
                      <InputSelect
                        label="Video Tips"
                        placeholder={loadingOptions ? "Loading..." : "Select tip"}
                        options={tipsOptions}
                        value={tipsOptions.find((opt) => opt.value === item.videoTips) || null}
                        onChange={(selected) =>
                          handleUpdateVideoItem(index, "videoTips", selected?.value || null)
                        }
                        isDisabled={loadingOptions}
                      />
                      <InputSelect
                        label="Video Page"
                        placeholder={loadingOptions ? "Loading..." : "Select page"}
                        options={pagesOptions}
                        value={pagesOptions.find((opt) => opt.value === item.videoPage) || null}
                        onChange={(selected) =>
                          handleUpdateVideoItem(index, "videoPage", selected?.value || null)
                        }
                        isDisabled={loadingOptions}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteVideoItem(index)}
                      className="mt-6 p-2 text-red-500 hover:bg-red-50 rounded">
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleAddVideoItem}
                  className="flex items-center gap-1 px-3 py-1 bg-[#606060] text-white rounded-md text-sm hover:bg-[#505050]">
                  <PlusIcon className="w-4 h-4" />
                  Add Video
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end items-center mt-4 border-t-2 pt-2">
        <Button.AdminPanelButton
          type="submit"
          isSave={true}
          loading={isFormSubmitting}
          bgColor="#515151"
          textColor="#FFFFFF"
          color="white"
          onClick={handleFormSubmit}>
          Save
        </Button.AdminPanelButton>
      </div>
    </>
  );
};

export default VideoEditor;
